import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    useMemo
} from 'react';
import Paginate from 'react-js-pagination';
import clsx from 'clsx';

import { searchApi } from '@api';
import { useRootDataContext } from '@context/RootData/RootDataContext';
import { useGlobalDataContext } from '@context/GlobalData/GlobalDataContext';
import { useLoading } from '@context/Loading/LoadingContext';
import { useSpecificationContext } from '@context/Specification';
import { useScreenContext } from '@context/Screen/ScreenContext';
import { useFilterInfoModalContext } from '@context/FilterInfoModal/FilterInfoModalContext';
import searchStates from '@config/searchStates';
import { adjustmentConfig, sortConfig } from '@config';
import { getPositionOfElement } from '@utils';
import { FaSort } from "react-icons/fa";

import { useURLParamsFacets } from '@hooks';

import {
    Results,
    Filters,
    FilterHeader,
    Sort,
    SortMobile,
    Selection,
    AdjustedModelOverlay,
    SpecificationsModalOverlay,
    PlaceholderSidebar,
    PlaceHolderResultCard,
    PlaceholderFacets,
    PlaceholderSortBar,
    FilterInfoModalOverlay,
} from './components';
import { NextChevron, PrevChevron, FilterIcon } from '../icons';
import styles from './Search.module.scss';
import stylesSort from './components/sort.module.scss';

/**
 * Component that returns the search
 * @returns {JSX.Element}
 */
const Search = () => {
    const containerRef = useRef(null);
    const formRef = useRef(null);

    const rootData = useRootDataContext();
    const { globalContext, setGlobalContext } = useGlobalDataContext();

    const screenSize = useScreenContext();

    const isMobile = useMemo(() => screenSize === 'mobile', [screenSize]);
    const isDesktop = useMemo(() => screenSize === 'desktop', [screenSize]);

    /**
     * Hook for getting and updating the URL search params.
     */
    const [paramsFacets, setParamsFacets] = useURLParamsFacets();

    /**
     * We use these search states to keep track of where we are in the process.
     * This also enables us to see what state the app was previously in what should happen next.
     */
    const { INIT, UPDATING, PAGINATING, SEARCHING, RESTING } = searchStates;

    const [data, setData] = useState({
        items: []
    });
    const [isShowFilter, setIsShowFilter] = useState(false);
    const [isShowMoreText, setIsShowMoreText] = useState(false);

    // open and close modal sort
    const [modalIsopen, setModalOpen] = useState(false);

    const { isFilterInfoModalOpen, setIsFilterInfoModalOpen, selectedFilterData, setCurrentSelectedFilter } = useFilterInfoModalContext();

    /** Show the adjusted modal overlay **/
    const [isShowAdjustedModal, setIsShowAdjustedModal] =
        useState(adjustmentConfig);

    const { loading, setLoading } = useLoading();
    const { carId } = useSpecificationContext();

    /**
     * Returns the offset of the banner element.
     * This element is outside of the React context.
     * @returns {number}
     */
    const getBannerOffset = () => {
        const banner = document.querySelector('header.header');
        if (banner === null) {
            return 0;
        }

        return banner.offsetHeight;
    };

    /**
     * Get the car the matches the id.
     * If no car is selected then return null.
     */
    const specificationCarData = useMemo(() => {
        if (carId === null || data.items === undefined) {
            return null;
        }

        return data.items
            .filter(({ type }) => type === 'occasion')
            .find(({ data }) => data.id.carId === carId);
    }, [data.items, carId]);

    /**
     * Perform a query to the server to get the results
     * based on the current facets.
     *
     * @returns {Promise<any[]>}
     */

    const searchOccasions = useCallback(async () => {
        const limit = rootData.limit ? rootData.limit : globalContext.limit;

        const filters = {
            start: (globalContext.start - 1) * limit,
            limit,
            facets: globalContext.facets,
            sort: globalContext.sort
        };

        try {
            const response = await searchApi(rootData, filters);
            return response.data;
        } catch (error) {
            console.error(error);

            return [];
        }
    }, [globalContext, rootData]);

    /**
     * Invoke when user clicks to request another page.
     * @param {Event} event
     */
    const handlePageClick = event => {
        setGlobalContext(({ searchState, ...prevContext }) => ({
            ...prevContext,
            start: event,
            searchState: PAGINATING,
            prevSearchState: searchState
        }));
    };

    /**
     * Debugging purpose.
     * Logs the current searchState in the debugger console.
     */
    // useEffect(() => {
    //     console.debug(globalContext.searchState.description);
    // }, [globalContext]);

    /**
     * Update Facets based on the URL.
     */
    useEffect(() => {
        if (globalContext.searchState === INIT) {
            setGlobalContext(({ searchState, ...prevContext }) => ({
                ...prevContext,
                searchState: SEARCHING,
                prevSearchState: searchState,
                facets: paramsFacets
            }));
        }
    }, [
        globalContext,
        setGlobalContext,
        paramsFacets,
        INIT,
        SEARCHING,
        RESTING
    ]);

    /**
     * Search when the context has changed after the first search.
     */
    useEffect(() => {
        if (
            globalContext.searchState === UPDATING &&
            globalContext.prevSearchState === RESTING
        ) {
            setGlobalContext(({ searchState, ...prevContext }) => ({
                ...prevContext,
                searchState: SEARCHING,
                prevSearchState: searchState
            }));
        }
    }, [globalContext, setGlobalContext, UPDATING, RESTING, SEARCHING]);

    /**
     * Update the URL based on the Facets.
     */
    useEffect(() => {
        if (
            globalContext.searchState === SEARCHING &&
            globalContext.prevSearchState === UPDATING
        ) {
            setParamsFacets(globalContext.facets);
        }
    }, [globalContext, setParamsFacets, SEARCHING, UPDATING]);

    /**
     * Change the loading state based on the current searchState.
     */
    useEffect(() => {
        if (
            globalContext.searchState === SEARCHING ||
            globalContext.searchState === PAGINATING
        ) {
            setLoading(true);
        } else if (globalContext.searchState === RESTING) {
            setLoading(false);
        }
    }, [globalContext, setLoading, SEARCHING, PAGINATING, RESTING]);

    /**
     * Do a search whenever the back button is pressed.
     * The previous facets are stored in the History state.
     */
    useEffect(() => {
        window.addEventListener('popstate', event => {
            setGlobalContext(prevState => ({
                ...prevState,
                facets: event.state,
                searchState: SEARCHING
            }));
        });
    }, [setGlobalContext, SEARCHING]);

    useEffect(() => {
        if (globalContext.searchState === PAGINATING) {
            const additionalPadding = 10;
            const bannerOffset = getBannerOffset();
            const { top } = getPositionOfElement(containerRef.current);

            window.scrollTo({
                top: top - bannerOffset - additionalPadding,
                left: 0,
                behavior: 'smooth'
            });
        }
    }, [globalContext, PAGINATING]);

    /**
     * Fetch the search results
     */
    useEffect(() => {
        if (
            globalContext.searchState === SEARCHING ||
            globalContext.searchState === PAGINATING
        ) {
            searchOccasions()
                .then(data => {
                    setIsShowAdjustedModal(prevState => ({
                        ...prevState,
                        adjustment_text_true: data.adjustment_text.true,
                        adjustment_text_false: data.adjustment_text.false
                    }));
                    setData(data);
                })
                .catch(() => setData({}))
                .finally(() => {
                    setGlobalContext(({ searchState, ...prevContext }) => ({
                        ...prevContext,
                        searchState: RESTING,
                        prevSearchState: searchState
                    }));
                });
        }
    }, [
        globalContext,
        setGlobalContext,
        setData,
        searchOccasions,
        SEARCHING,
        PAGINATING,
        RESTING
    ]);

    const openFilter = useCallback(() => {
        document.documentElement.classList.add(`op-filter_open`);

        setIsShowFilter(true);
    }, [setIsShowFilter]);

    const closeFilter = useCallback(() => {
        document.documentElement.classList.remove(`op-filter_open`);

        setIsShowFilter(false);
    }, [setIsShowFilter]);

    const showMoreText = useCallback(() => {
        setIsShowMoreText(true);
    }, [setIsShowMoreText]);

    const hideMoreText = useCallback(() => {
        setIsShowMoreText(false);
    }, [setIsShowMoreText]);

    /**
     * The label of the sort item that is currently active
     * @var {string}
     */
    const currentSortLabel = useMemo(() => {
        const sortItem = sortConfig.find(
            ({ value }) => value === globalContext.sort
        );
        if (typeof sortItem === 'undefined') {
            return 'Sorteren';
        }

        return sortItem.label;
    }, [sortConfig, globalContext.sort]);

    const OpenSortModal = () => {
        setModalOpen(true);
    };

    const CloseSortModal = () => {
        setModalOpen(false);
    };

    /**
     * Make cookie from url
     */
    const getURL = window.location.pathname + window.location.search;
    localStorage.setItem('localGetURL', getURL);
    const localGetURL = localStorage.getItem('localGetURL');

    let exdays;
    const dateCookie = new Date();
    dateCookie.setTime(dateCookie.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = 'expires=' + dateCookie.toUTCString();
    document.cookie =
        // eslint-disable-next-line no-useless-concat
        'localurl' + '=' + localGetURL + ';' + expires + ';path=/';

    /**
     * Render the Search component
     */
    return (
        <>
            <div ref={containerRef} className={clsx('op-container')}>
                <dynamic-banner class={`op-search__top ${styles.countSort}`} room>
                    {isDesktop && !loading && (
                        <>
                            <div className="op-search__count d-flex">
                                {data?.count} occasions gevonden
                            </div>

                            <div className="op-search__sort d-flex">
                                <Sort />
                            </div>
                        </>
                    )}

                    {isMobile && !loading && (
                        <>
                            <div className="op-search__count__mobile">
                                <button
                                    className="op-search__count__mobile-text"
                                    onClick={openFilter}
                                >
                                    <FilterIcon />
                                    Filter
                                </button>
                            </div>

                            <div className="op-sort_mobile">
                                <button
                                    onClick={OpenSortModal}
                                    className="op-sort_mobile__button"
                                >   
                                    <FaSort />
                                    {currentSortLabel}
                                </button>
                            </div>
                        </>
                    )}
                    {loading && <PlaceholderSortBar />}
                </dynamic-banner>

                {!loading && (
                    <form ref={formRef} className="op-sidebar">
                        <FilterHeader
                            setData={setData}
                            closeFilter={closeFilter}
                        />

                        {<Filters facets={data.facets} setIsFilterInfoModalOpen={setIsFilterInfoModalOpen} setCurrentSelectedFilter={setCurrentSelectedFilter} />}

                        <div
                            className={`${clsx(
                                `op-filter-fixed`,
                                isShowFilter && `op-filter-fixed_open`
                            )}`}
                        >
                            {isShowFilter && (
                                <button
                                    className={`op-filter-button`}
                                    onClick={closeFilter}
                                >
                                    Bekijk {data?.count} occasions
                                </button>
                            )}
                        </div>
                    </form>
                )}

                {loading && isDesktop && <PlaceholderSidebar />}

                <div className="op-search__inner">
                    {loading && paramsFacets ? (
                        <PlaceholderFacets />
                    ) : (
                        <Selection facets={data.facets} />
                    )}
                    {loading ? (
                        <ul className="op-results">
                            {Array(7)
                                .fill(null)
                                .map((_, i) => (
                                    <PlaceHolderResultCard key={i} />
                                ))}
                        </ul>
                    ) : (
                        <Results
                            setIsShowAdjustedModal={setIsShowAdjustedModal}
                            data={data.items}
                        />
                    )}

                    <div className="op-pagination">
                        <Paginate
                            activePage={globalContext?.start}
                            itemsCountPerPage={
                                rootData?.limit
                                    ? Number(rootData.limit)
                                    : Number(globalContext.limit)
                            }
                            totalItemsCount={data?.count || 0}
                            hideFirstLastPages={true}
                            onChange={handlePageClick}
                            innerClass="op-pagination__list"
                            activeLinkClass="op-pagination__list--active"
                            disabledClass="op-pagination__list--disabled"
                            itemClass="op-pagination__list-item"
                            itemClassPrev="op-pagination__list-item--prev"
                            itemClassNext="op-pagination__list-item--next"
                            linkClass="op-pagination__link"
                            prevPageText={<PrevChevron />}
                            nextPageText={<NextChevron />}
                        />
                    </div>

                    <div className={`op-seo`}>
                        <h2
                            dangerouslySetInnerHTML={{
                                __html: rootData.infoTitle
                            }}
                        />
                        <div
                            dangerouslySetInnerHTML={{
                                __html: rootData.infoContent
                            }}
                        />
                        {isShowMoreText && rootData.infoContentExpand && (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: rootData.infoContentExpand
                                }}
                            />
                        )}
                        {rootData.infoContentExpand &&
                            <>
                                {!isShowMoreText ? (
                                    <button
                                        className={`op-seo_button`}
                                        onClick={showMoreText}
                                    >
                                        Lees meer +
                                    </button>
                                ) : (
                                    <button
                                        className={`op-seo_button`}
                                        onClick={hideMoreText}
                                    >
                                        Lees minder -
                                    </button>
                                )}
                            </>
                        }
                    </div>
                </div>
            </div>

            {isShowAdjustedModal.adjustment_modal === true && (
                <AdjustedModelOverlay
                isShowAdjustedModal={isShowAdjustedModal}
                setIsShowAdjustedModal={setIsShowAdjustedModal}
                />
                )}
            {specificationCarData && (
                <SpecificationsModalOverlay car={specificationCarData.data} />
                )}
            {modalIsopen && <SortMobile OnclickCloseModal={CloseSortModal} />}
            {isFilterInfoModalOpen && <FilterInfoModalOverlay setShowModal={setIsFilterInfoModalOpen} filterData={selectedFilterData}/>}
        </>
    );
};

export default Search;
